/*
 * @Author: Claude
 * @Date: 2025-11-15
 * @Description: 图片/视频分辨率校验
 */
import { t } from '@/utils/helper/intl';

import { FileUploadRule } from '../types/validator';
import { getMediaDimensions } from '../utils/getMediaDimensions';
import {
  isResolutionPreset,
  normalizeResolutionDimensions,
  parseResolution,
} from '../utils/parseResolution';

/**
 * 校验图片或视频分辨率
 * @param file - 待校验的文件
 * @param rule - 校验规则
 * @returns Promise<string | null> - 错误信息或 null
 */
export const validateMediaResolution = async (
  file: File,
  rule: FileUploadRule,
): Promise<string | null> => {
  const { minResolution, maxResolution, message } = rule;

  // 如果没有设置任何分辨率相关的规则，跳过校验
  if (!minResolution && !maxResolution) {
    return null;
  }

  try {
    const { width, height } = await getMediaDimensions(file);

    const isBelowMinResolution = (
      targetResolution: string,
      targetWidth: number,
      targetHeight: number,
    ) => {
      const currentResolution = { width, height };

      if (isResolutionPreset(targetResolution)) {
        const current = normalizeResolutionDimensions(currentResolution);
        const target = normalizeResolutionDimensions({
          width: targetWidth,
          height: targetHeight,
        });

        return current.shortEdge < target.shortEdge || current.longEdge < target.longEdge;
      }

      return width < targetWidth || height < targetHeight;
    };

    const isAboveMaxResolution = (
      targetResolution: string,
      targetWidth: number,
      targetHeight: number,
    ) => {
      const currentResolution = { width, height };

      if (isResolutionPreset(targetResolution)) {
        const current = normalizeResolutionDimensions(currentResolution);
        const target = normalizeResolutionDimensions({
          width: targetWidth,
          height: targetHeight,
        });

        return current.shortEdge > target.shortEdge || current.longEdge > target.longEdge;
      }

      return width > targetWidth || height > targetHeight;
    };

    // 1. 校验最小分辨率
    if (minResolution) {
      const minRes = parseResolution(minResolution);
      if (!minRes) {
        return minResolution;
      }

      if (isBelowMinResolution(minResolution, minRes.width, minRes.height)) {
        return (
          message ||
          t('minResolutionminWidthxminHeight-widthxheight', {
            minResolution,
            minWidth: minRes.width,
            minHeight: minRes.height,
            width,
            height,
          })
        );
      }
    }

    // 2. 校验最大分辨率
    if (maxResolution) {
      const maxRes = parseResolution(maxResolution);
      if (!maxRes) {
        return maxResolution;
      }

      if (isAboveMaxResolution(maxResolution, maxRes.width, maxRes.height)) {
        return (
          message ||
          t('maxResolutionmaxWidthxmaxHeight-widthxheight', {
            maxResolution,
            maxWidth: maxRes.width,
            maxHeight: maxRes.height,
            width,
            height,
          })
        );
      }
    }

    return null;
  } catch (error) {
    return `${error instanceof Error ? error.message : t('merror.httpError.12301000')}`;
  }
};
